import { useEffect, useState } from 'react'
import { useFloatingOffset } from '@/shared/hooks/use-floating-offset'
import { useLocation, useParams } from 'react-router-dom'
import { useNav } from '@/shared/hooks/use-nav'
import { MapPin, Calendar, Clock, Check, X, Share2, Shield, Navigation, Pencil, Globe, RotateCcw, MessageCircle, UserPlus, Minus, Plus } from 'lucide-react'
import { TopBar } from '@/shared/components/top-bar'
import { IconButton } from '@/shared/ui/icon-button'
import { Chip } from '@/shared/ui/chip'
import { MatchTypeBadge } from '@/shared/ui/match-type-badge'
import { Card } from '@/shared/ui/card'
import { Avatar } from '@/shared/ui/avatar'
import { Button } from '@/shared/ui/button'
import { BottomSheet } from '@/shared/ui/bottom-sheet'
import { EmptyState } from '@/shared/ui/empty-state'
import { ErrorState } from '@/shared/ui/error-state'
import { MatchCardSkeleton } from '@/shared/components/match-card-skeleton'
import { PlayerProfileSheet } from '@/features/jugadores/player-profile-sheet'
import { PostMatchPanel } from './post-match-panel'
import { formatNivelRange, LEVEL_BANDS } from '@/shared/levels'
import { formatReliability } from '@/shared/format'
import { shareMatch, matchShareUrl, matchShareText, directionsUrl } from '@/shared/share'
import { showToast } from '@/shared/ui/toast'
import { getPlayerActions, onPlayerActionsChange } from '@/shared/player-actions'
import { findMatch, freeSlots, getProfile, joinMatch, leaveMatch, overrideMatch } from '@/shared/mock/data'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { useAuthStore } from '@/core/auth/auth-store'
import { useMyProfile } from '@/features/perfil/use-my-profile'
import { NeedsGenderSheet } from './needs-gender-sheet'
import { useAddPlaceholder, useMatch, useJoinMatch, useLeaveMatch, useCancelMatch, useAdjustSeats, dayLabelWithDate } from './use-matches'
import { ContactInviteSheet, type InviteeSeed } from './contact-invite-sheet'
import { usePublicProfile } from '@/features/jugadores/use-public-profile'
import { vouchingExhausted } from '@/core/models/seat-identity'
import { PushNudge } from '@/shared/components/push-nudge'
import { MembershipError } from './api/matches-repository'
import { track } from '@/core/analytics'
import { trackShare, trackPlaceholderCreated } from '@/core/funnel'

// Match detail — full match view + join/share, plus ORGANIZER MODE on the
// player's own match (editar / cancelar / abrir al público). Join is optimistic
// on mock data (the slot fills, count updates, CTA confirms, haptic fires);
// swaps to a Cloud-Function seat-claim later.
export function MatchDetailPage() {
  // Publishes this screen's bottom chrome height so the update banner and
  // toasts float ABOVE it instead of covering the primary action.
  const setBottomChrome = useFloatingOffset()
  const { id } = useParams()
  const navigate = useNav()
  const [, forceRender] = useState(0)
  // Reliability lines re-render live when a no-show flag lands in the store.
  useEffect(() => onPlayerActionsChange(() => forceRender((n) => n + 1)), [])
  const profile = getProfile()
  const user = useAuthStore((st) => st.user)
  const myDoc = useMyProfile()
  const realMatch = useMatch(id)
  const organizerPub = usePublicProfile(AUTH_BYPASS ? null : realMatch.data?.organizerUid)
  const joinMutation = useJoinMatch()
  const leaveMutation = useLeaveMatch()
  const cancelMutation = useCancelMatch()
  const match: import('./use-matches').MatchView | null = AUTH_BYPASS
    ? (findMatch(id) ?? null)
    : (realMatch.data ?? null)
  // Membership truth: UIDs under real auth; the mock store's names in walks.
  const joined = AUTH_BYPASS
    ? (match?.players.some((p) => p.name === profile.name) ?? false)
    : ((match?.playerUids?.includes(user?.uid ?? '') && match?.organizerUid !== user?.uid) ?? false)
  const [justJoined, setJustJoined] = useState(false)
  // Name drives the mock branch; uid switches the sheet to players_public.
  const [profileFor, setProfileFor] = useState<{ name: string; uid: string | null } | null>(null)
  const [cancelSheet, setCancelSheet] = useState(false)
  // null = sheet closed. '' = open and empty (the free-seat tap).
  const [seatingName, setSeatingName] = useState<string | null>(null)
  const [kickFor, setKickFor] = useState<string | null>(null)
  const [leaveSheet, setLeaveSheet] = useState(false)
  const [genderSheet, setGenderSheet] = useState(false)
  const [justOpened, setJustOpened] = useState(false)
  const [copied, setCopied] = useState(false)
  const [shareFallback, setShareFallback] = useState(false)
  // Invite sheet (spec/02): Repetir lands here with the previous roster in
  // router state → auto-open preselected; the fill-assist button opens empty.
  // `openInvites` arrives from Crear for a PRIVATE match: nobody can join one
  // until somebody is invited, so the sheet opens empty rather than leaving the
  // organizer on a match with no way in.
  const navState = useLocation().state as {
    reinvite?: InviteeSeed[]
    openInvites?: boolean
  } | null
  const reinvite = navState?.reinvite
  const openInvites = navState?.openInvites
  const [inviteOpen, setInviteOpen] = useState(false)
  useEffect(() => {
    if (reinvite?.length || openInvites) setInviteOpen(true)
  }, [reinvite, openInvites])
  const adjustSeats = useAdjustSeats()
  const addPlaceholder = useAddPlaceholder()
  // My own vouching record — the cap is enforced in rules, but a limit the
  // player cannot see is a limit they experience as a bug.
  const myPub = usePublicProfile(AUTH_BYPASS ? null : (user?.uid ?? null))
  // `vouchingExhausted` and NOT the arithmetic inline: the rule lives in one
  // place with its own tests, and a second copy here would be a second place
  // for it to drift — which is exactly what the rules/CF/client trio already
  // costs us once. A player with no projection doc yet is not blocked.
  const vouchBlocked = !AUTH_BYPASS && myPub.data != null && vouchingExhausted(myPub.data)

  async function onSeatSomeone() {
    const name = seatingName?.trim()
    if (!name || !user?.uid || !match || addPlaceholder.isPending) return
    navigator.vibrate?.(12)
    try {
      await addPlaceholder.mutateAsync({ matchId: match.id, name, createdBy: user.uid })
      trackPlaceholderCreated(match.id, Math.max(freeSlots(match) - 1, 0))
      setSeatingName(null)
      showToast(`${name} ocupa una plaza — compártele el enlace`)
    } catch (e) {
      navigator.vibrate?.(20)
      const reason = e instanceof MembershipError ? e.reason : ''
      // "Inténtalo de nuevo" was the only message here, and for the one reason
      // that never resolves by retrying it was actively wrong advice — the same
      // dead end `needs-gender` used to be on the join path.
      showToast(SEAT_BLOCK_MESSAGES[reason] ?? 'No pudimos apuntarle. Inténtalo de nuevo.')
    }
  }


  if (!AUTH_BYPASS && realMatch.isPending) {
    return (
      <>
        <TopBar title="Partido" back />
        {/* A literally empty div was the loading state — a blank screen with no
            indication anything was happening. A card-shaped skeleton says
            "a match is arriving" and holds the layout so nothing jumps. */}
        <div className="flex flex-col gap-3 px-4 py-3" aria-busy>
          <MatchCardSkeleton />
        </div>
      </>
    )
  }

  // A FAILED READ IS NOT A MISSING MATCH. Without this branch the error fell
  // through to `!match` and told the player "No encontramos este partido" —
  // the app reporting a transient network blip as a deleted match, which
  // sends them away instead of letting them retry.
  if (!AUTH_BYPASS && realMatch.isError) {
    return (
      <>
        <TopBar title="Partido" back />
        <ErrorState
          title="No pudimos cargar el partido."
          onRetry={() => void realMatch.refetch()}
        />
      </>
    )
  }

  if (!match) {
    return (
      <>
        <TopBar title="Partido" back />
        <EmptyState title="No encontramos este partido." tone="faint" />
      </>
    )
  }

  const isOrganizer = AUTH_BYPASS ? match.organizer === profile.name : match.organizerUid === user?.uid
  const isPast = match.status === 'past'
  const isCancelled = match.status === 'cancelled'
  const privacy = match.privacy
  const players = match.players
  const free = freeSlots(match)
  const full = free <= 0 && !joined

  const SEAT_BLOCK_MESSAGES: Record<string, string> = {
    'vouch-limit':
      'Has apuntado a todos los que puedes por ahora. Recuperas plazas cuando alguno juegue.',
    full: 'Ya no queda plaza libre en este partido.',
    'not-active': 'Este partido ya no admite jugadores.',
    'not-a-player': 'Apúntate tú primero para poder apuntar a alguien.',
  }

  const JOIN_BLOCK_MESSAGES: Record<string, string> = {
    full: 'El partido ya está completo.',
    'not-active': 'Este partido ya no admite jugadores.',
    'needs-gender': 'Este partido es por categoría — dinos si eres hombre o mujer.',
    'wrong-gender': 'Este partido está limitado a otra categoría.',
    // Names WHICH side is full, because "no hay sitio" on a match showing free
    // seats reads as a bug. A mixto keeps 2+2, so the remaining seats can be
    // real and still not yours.
    'mixed-side-full': 'Es un partido mixto (2 y 2) y ya no quedan plazas de tu categoría.',
    'already-joined': 'Ya estás en este partido.',
  }

  /**
   * `genderOverride` — the value the needs-gender sheet just wrote.
   *
   * The retry used to be `myDoc.refetch().then(() => toggleJoin())`, which
   * re-failed with the SAME needs-gender every time: `refetch()` updates the
   * query cache and re-renders, but the `.then()` already running still closes
   * over the myDoc snapshot from the render that created it. A stale closure
   * racing its own refetch.
   *
   * Passing the value removes the race instead of widening the window — the
   * sheet knows what it saved, so nothing has to settle first.
   */
  const toggleJoin = async (genderOverride?: 'male' | 'female') => {
    if (!joined) {
      navigator.vibrate?.(12)
      if (AUTH_BYPASS) {
        joinMatch(match.id)
        setJustJoined(true)
        forceRender((n) => n + 1)
        return
      }
      if (!user) return
      try {
        await joinMutation.mutateAsync({
          matchId: match.id,
          joiner: {
            uid: user.uid,
            name: myDoc.data?.profile.name ?? profile.name,
            photoURL: user.photoURL,
            gender:
              genderOverride ??
              ((myDoc.data?.padel.gender as 'male' | 'female' | null | undefined) ?? null),
          },
        })
        setJustJoined(true)
        track('match_joined', { match_id: match.id })
      } catch (e) {
        navigator.vibrate?.(20)
        const reason = e instanceof MembershipError ? e.reason : ''
        // ASK IN PLACE. This used to toast and then silently
        // navigate('/editar-perfil') — a screen identical to the one you reach
        // from the Perfil tab, with nothing explaining why you arrived and
        // nothing pointing at "Género (opcional)". The persona walk called it
        // indistinguishable from a broken button, and it was: the toast rarely
        // survived the navigation either. Two taps here, no lost place in the
        // flow, and the join is retried for them.
        if (reason === 'needs-gender') {
          setGenderSheet(true)
        } else {
          showToast(JOIN_BLOCK_MESSAGES[reason] ?? 'No pudimos apuntarte. Inténtalo de nuevo.')
        }
      }
    } else {
      // Leaving affects 3 other people — confirm it (join stays 1-tap).
      setLeaveSheet(true)
    }
  }

  const confirmLeave = async () => {
    navigator.vibrate?.(15)
    if (AUTH_BYPASS) {
      leaveMatch(match.id)
      setLeaveSheet(false)
      setJustJoined(false)
      forceRender((n) => n + 1)
      showToast('Has salido del partido')
      return
    }
    if (!user) return
    try {
      await leaveMutation.mutateAsync({ matchId: match.id, uid: user.uid })
      track('match_left', { match_id: match.id })
      setLeaveSheet(false)
      setJustJoined(false)
      showToast('Has salido del partido')
    } catch {
      setLeaveSheet(false)
      navigator.vibrate?.(20)
      showToast('No pudimos sacarte del partido. Inténtalo de nuevo.')
    }
  }

  const openToPublic = () => {
    navigator.vibrate?.(12)
    // One fact, one store: Editar and every card see the new privacy too.
    overrideMatch(match.id, { privacy: 'Abierto' })
    setJustOpened(true)
  }

  const confirmCancel = async () => {
    navigator.vibrate?.(20)
    if (!AUTH_BYPASS) {
      try {
        await cancelMutation.mutateAsync(match.id)
      } catch {
        setCancelSheet(false)
        showToast('No pudimos cancelar. Inténtalo de nuevo.')
        return
      }
    } else {
      overrideMatch(match.id, { status: 'cancelled' })
    }
    track('match_cancelled', { match_id: match.id })
    setCancelSheet(false)
    showToast('Partido cancelado — avisamos a los jugadores')
    // Fresh tab (push tap / shared link): nothing to pop, so land on their
    // matches rather than about:blank.
    void navigate(-1, { fallback: '/mis-partidos' })
  }

  const onAdjustSeats = (delta: 1 | -1) => {
    navigator.vibrate?.(8)
    if (AUTH_BYPASS) {
      // Mock model stores companions; free is derived (capacity − players − companions).
      overrideMatch(match.id, { offAppCompanions: Math.max((match.offAppCompanions ?? 0) - delta, 0) })
      forceRender((n) => n + 1)
      showToast(delta === 1 ? 'Plaza abierta — compártelo' : 'Plaza cerrada')
      return
    }
    adjustSeats.mutate(
      { matchId: match.id, delta },
      {
        onSuccess: () =>
          showToast(delta === 1 ? 'Plaza abierta — compártelo' : 'Plaza cerrada'),
        onError: () => showToast('No se pudo ajustar — recarga e inténtalo'),
      },
    )
  }

  const onShare = async () => {
    navigator.vibrate?.(8)
    const text = `¡Te invito a un partido de pádel! ${matchShareText(match)}`
    const outcome = await shareMatch(match.id, text)
    // `outcome` rides along because 'blocked' and 'failed' mean no link left
    // the app at all — without it those would inflate the share count and make
    // the opens-per-share ratio look worse than the loop actually is.
    trackShare(match.id, outcome === 'copied' ? 'clipboard' : 'sheet', free, outcome)
    if (outcome === 'copied') {
      setCopied(true)
      showToast('Enlace copiado — pégalo donde quieras')
      setTimeout(() => setCopied(false), 2000)
    } else if (outcome === 'failed' || outcome === 'blocked') {
      // No share sheet AND clipboard denied, or the browser refused to open
      // the sheet — never fail silently: surface the link so the player can
      // copy it by hand.
      setShareFallback(true)
    }
  }

  return (
    <>
      <TopBar
        title="Partido"
        back
        actions={
          <IconButton label="Compartir partido" onClick={() => void onShare()}>
            <Share2 size={20} />
          </IconButton>
        }
      />

      <div className="flex-1 px-4 py-3 flex flex-col gap-4 pb-28">
        <div>
          {match.padelPlaceId ? (
            <button
              type="button"
              className="text-[length:var(--fs-xl)] font-bold text-left underline-offset-4 hover:underline"
              onClick={() => navigate(`/club/${match.padelPlaceId}`)}
            >
              {match.place}
            </button>
          ) : (
            <h2 className="text-[length:var(--fs-xl)] font-bold">{match.place}</h2>
          )}
          <p className="flex items-center gap-1 text-[color:var(--fg-muted)]">
            <MapPin size={14} /> {match.city}
            {match.distanceKm != null && (
              <span className="text-[color:var(--fg-faint)]">· a {match.distanceKm} km</span>
            )}
          </p>
        </div>

        {justJoined && (
          <Card className="um-enter border-[var(--ok)]">
            <div className="p-4 flex flex-col gap-3">
              <p className="flex items-center gap-2 font-bold text-[color:var(--ok)]">
                <Check size={18} /> ¡Estás dentro!
              </p>
              <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
                {match.dayLabel} a las {match.timeLabel} en {match.place}. Te esperan{' '}
                {match.players.map((p) => p.name.split(' ')[0]).join(', ')}.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="pill"
                  className="flex-1"
                  onClick={() => window.open(directionsUrl(`${match.place} ${match.city}`), '_blank')}
                >
                  <Navigation size={16} /> Cómo llegar
                </Button>
                <Button variant="outline" size="pill" className="flex-1" onClick={() => void onShare()}>
                  {copied ? <>Enlace copiado</> : <><Share2 size={16} /> Compartir</>}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {isOrganizer && free > 0 && !isPast && !isCancelled && (
          <Card>
            <div className="p-4 flex flex-col gap-2">
              <p className="font-semibold">Faltan {free} — compártelo para llenarlo</p>
              <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
                Envía el partido a tu grupo con el formato de siempre.
              </p>
              {/* Two channels: in-app contacts + WhatsApp. "Otro canal" lives
                  in the top-bar share icon — a third button here overflowed
                  390px AND duplicated that affordance. */}
              <div className="flex gap-2">
                <Button variant="outline" size="pill" className="flex-1" onClick={() => setInviteOpen(true)}>
                  <UserPlus size={16} /> Invitar
                </Button>
                <Button
                  variant="outline"
                  size="pill"
                  className="flex-1"
                  onClick={() => {
                    trackShare(match.id, 'whatsapp', free, 'opened')
                    window.open(
                      `https://wa.me/?text=${encodeURIComponent(`${matchShareText(match)} ${matchShareUrl(match.id)}`)}`,
                      '_blank',
                    )
                  }}
                >
                  <Share2 size={16} /> WhatsApp
                </Button>
              </div>
            </div>
          </Card>
        )}

        <Card>
          <div className="p-4 grid grid-cols-2 gap-3">
            <Info icon={<Calendar size={16} />} label="Día" value={dayLabelWithDate(match.dayLabel, match.dateISO)} />
            <Info icon={<Clock size={16} />} label="Hora" value={match.timeLabel} />
          </div>
        </Card>

        <div className="flex flex-wrap gap-2">
          {/* Informational fact, not a selection — accent-active is reserved
              for chosen/filter states (styleguide §0 similarity). */}
          <Chip>{formatNivelRange(match.levelMin, match.levelMax)}</Chip>
          <MatchTypeBadge type={match.matchType} />
          {free > 0 && match.position !== 'Indiferente' && <Chip>Busca {match.position}</Chip>}
          {/* Display-only mapping. `privacy` stays 'Abierto' internally — it is
              a type value compared in 11 places, and renaming it to fix a
              LABEL would churn logic to solve a copy problem. */}
          <Chip>{privacy === 'Privado' ? 'Privado' : 'Público'}</Chip>
          {/* The audience rule was INVISIBLE here — the feed card and Mis
              partidos both showed it, the one screen a player reads before
              joining did not. Combined with the privacy chip saying "Abierto",
              a Mixto match read as "anyone can join". Omitted for 'open' so the
              common case stays quiet. */}
          {match.genderCategory != null && match.genderCategory !== 'open' && (
            <Chip>
              {match.genderCategory === 'women'
                ? 'Femenino'
                : match.genderCategory === 'men'
                  ? 'Masculino'
                  : 'Mixto · 2 y 2'}
            </Chip>
          )}
          {match.cost != null && <Chip>{match.cost.toFixed(2).replace('.', ',')} €/jugador</Chip>}
          <Chip className={match.courtReserved ? 'text-[color:var(--ok)] border-[var(--ok)]' : ''}>
            {match.courtReserved ? <Check size={13} /> : <X size={13} />}
            {match.courtReserved ? 'Con pista' : 'Sin pista'}
          </Chip>
        </div>

        {isCancelled && (
          <Card className="border-[var(--bad)]">
            <p className="p-4 flex items-center gap-2 font-semibold text-[color:var(--bad)]">
              <X size={17} /> Partido cancelado — los jugadores han sido avisados.
            </p>
          </Card>
        )}

        {isOrganizer && !isCancelled && privacy === 'Privado' && free > 0 && (
          <Card className="border-[var(--accent)]">
            <div className="p-4 flex flex-col gap-3">
              <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
                Partido privado con {free === 1 ? '1 plaza libre' : `${free} plazas libres`} — ¿no
                encuentras jugadores entre tus contactos?
              </p>
              <Button variant="outline" size="pill" onClick={openToPublic}>
                <Globe size={16} /> Abrir al público
              </Button>
            </div>
          </Card>
        )}
        {isOrganizer && justOpened && (
          <p className="um-enter flex items-center gap-2 text-[length:var(--fs-sm)] text-[color:var(--ok)]">
            <Check size={15} /> Ahora es un partido abierto — aparecerá en el feed.
          </p>
        )}

        {match.note && <p className="text-[color:var(--fg-muted)]">{match.note}</p>}

        {/* Members-only coordination bridge (spec/02 #11): the group link is
            the reward for joining — never shown to feed browsers/preview. */}
        {/* In-context push ask (R1): only members see it, only while the
            match is upcoming — the moment notifications start mattering. */}
        {!isPast && !isCancelled && (joined || isOrganizer) && <PushNudge />}

        {match.whatsappGroupUrl && (joined || isOrganizer) && (
          <a
            href={match.whatsappGroupUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="um-enter self-start inline-flex items-center gap-2 min-h-[var(--tap)] px-4 rounded-[var(--radius)] border border-[var(--line)] font-medium hover:bg-[var(--line-soft)] active:scale-[0.98] transition-transform"
          >
            <MessageCircle size={17} className="text-[color:var(--ok)]" /> Abrir grupo de WhatsApp
          </a>
        )}

        <div>
          {/* `capacity - free`, NOT `players.length`. Off-app companions occupy
              real seats: freeSlots() subtracts them and the match card already
              counts this way, but this header didn't — so an organizer who
              declared 3 companions with "+ Plaza" saw "Jugadores · 1/4" on a
              match that was genuinely full and had already stopped offering
              "Faltan 1". One fact, one formula. */}
          <p className="text-[length:var(--fs-sm)] font-semibold text-[color:var(--fg-muted)] mb-2">
            Jugadores · {match.capacity - free}/{match.capacity}
          </p>
          <Card className="divide-y divide-[var(--line)]">
            {players.map((p, i) => {
              const you = p.name === 'Tú' || p.name === profile.name
              const canKick = isOrganizer && !isPast && !isCancelled && !you
              return (
                <div key={`${p.name}-${i}`} className={`flex items-center gap-1 pr-2 ${you ? 'um-enter' : ''}`}>
                  <button
                    type="button"
                    disabled={you}
                    onClick={() => setProfileFor({ name: p.name, uid: match.playerUids?.[i] ?? null })}
                    className={`flex-1 min-w-0 flex items-center gap-3 px-4 py-2.5 text-left ${
                      you ? '' : 'hover:bg-[var(--line-soft)] active:scale-[0.99] transition-transform'
                    }`}
                  >
                    {/* A placeholder gets a dotted ring, not an avatar: it is
                        occupied but unclaimed, and the compact SeatStrip already
                        draws that distinction. This row is the FULLEST view of
                        the roster, so it is the one place the attribution must
                        be spelled out — "invitado por Lucía" is where
                        accountability becomes legible to somebody deciding
                        whether to join (spec/02 § What the player sees). */}
                    {p.identity === 'placeholder' ? (
                      <span className="h-9 w-9 shrink-0 rounded-full border border-dotted border-[var(--line-strong)] bg-[var(--surface-raised)] inline-flex items-center justify-center text-[length:var(--fs-xs)] font-semibold text-[color:var(--fg-muted)]">
                        {p.name.trim().charAt(0).toUpperCase() || '?'}
                      </span>
                    ) : (
                      <Avatar name={p.name} size={36} />
                    )}
                    <span className="flex-1 min-w-0">
                      <span className="block font-medium truncate">{p.name}</span>
                      {p.identity === 'placeholder' && (
                        <span className="block text-[length:var(--fs-xs)] text-[color:var(--fg-faint)] truncate">
                          {p.createdByName ? `Invitado por ${p.createdByName}` : 'Invitado'} · sin confirmar
                        </span>
                      )}
                    </span>
                    {p.name === match.organizer && <Chip>Organiza</Chip>}
                    {you && <Chip className="text-[color:var(--ok)] border-[var(--ok)]">Tú</Chip>}
                  </button>
                  {/* Organizer lever: remove ONE player without cancelling the
                      whole match (cert-walk-11, Carlos's top pain). */}
                  {canKick && (
                    <IconButton
                      label={`Quitar a ${p.name}`}
                      onClick={() => setKickFor(p.name)}
                      className="shrink-0 text-[color:var(--fg-faint)]"
                    >
                      <X size={16} />
                    </IconButton>
                  )}
                </div>
              )
            })}
            {/* ONE ROW PER COMPANION, not a collapsed "+2". Each of these is a
                real person who will be on the court; collapsing them made two
                occupied seats read as a footnote, and left the roster showing
                fewer boxes than the "N/4" above it. */}
            {/* LEGACY only: matches written before the identity ladder still
                carry an anonymous count. New seats are real rows above, with a
                name and a voucher. */}
            {Array.from({ length: match.offAppCompanions ?? 0 }).map((_, i) => (
              <div key={`companion-${i}`} className="flex items-center gap-3 px-4 py-2.5 text-[color:var(--fg-muted)]">
                <span className="h-9 w-9 rounded-full border border-[var(--line-strong)] bg-[var(--surface-raised)] inline-flex items-center justify-center text-[length:var(--fs-xs)] font-semibold">
                  +
                </span>
                <span>Invitado del organizador (fuera de la app)</span>
              </div>
            ))}
            {Array.from({ length: Math.max(free, 0) }).map((_, i) => (
              <div key={`free-${i}`} className="flex items-center gap-3 px-4 py-2.5 text-[color:var(--fg-faint)]">
                <span className="h-9 w-9 rounded-full border border-dashed border-[var(--line-strong)]" />
                {/* The free seat IS the affordance. "Voy con mi compañero" is
                    the most common case in padel, and any joined player may do
                    it — not just the organizer, who would otherwise be
                    everyone's administrator (spec/02 § Who may add). */}
                {i === 0 && joined && !isPast && !isCancelled ? (
                  <button
                    type="button"
                    onClick={() => setSeatingName('')}
                    className="min-h-[var(--tap)] text-left text-[color:var(--accent)] font-semibold"
                  >
                    Plaza libre · apuntar a alguien
                  </button>
                ) : (
                  <span>Plaza libre</span>
                )}
              </div>
            ))}
          </Card>
          {/* Companion seat adjust (spec/02, 2026-07-05): an off-app
              companion drops → open their seat; a 4th found off-app →
              close one. Only where it can apply (some companion or free
              seat exists) — a plain 4-player match never shows it. */}
          {isOrganizer &&
            !isPast &&
            !isCancelled &&
            ((match.offAppCompanions ?? 0) > 0 || free > 0) && (
              <div className="mt-2 flex items-center gap-2">
                <p className="flex-1 text-[length:var(--fs-xs)] text-[color:var(--fg-faint)]">
                  Ajusta las plazas libres
                </p>
                {/* Both buttons read "Plaza" to a screen reader — the only
                    difference was a Minus vs Plus glyph, which carries no text
                    alternative. An organizer using VoiceOver could not tell
                    which one closes a seat. aria-label names the action. */}
                <Button
                  variant="outline"
                  size="pill"
                  aria-label="Cerrar una plaza"
                  disabled={free <= 0 || adjustSeats.isPending}
                  onClick={() => onAdjustSeats(-1)}
                >
                  <Minus size={15} /> Plaza
                </Button>
                <Button
                  variant="outline"
                  size="pill"
                  aria-label="Abrir una plaza"
                  disabled={(match.offAppCompanions ?? 0) <= 0 || adjustSeats.isPending}
                  onClick={() => onAdjustSeats(1)}
                >
                  <Plus size={15} /> Plaza
                </Button>
              </div>
            )}
        </div>

        <button
          type="button"
          onClick={() => setProfileFor({ name: match.organizer, uid: match.organizerUid ?? null })}
          className="flex items-center gap-2 text-[length:var(--fs-sm)] text-[color:var(--fg-muted)] min-h-[var(--tap)] rounded-[var(--radius)] hover:bg-[var(--line-soft)] active:scale-[0.99] transition-transform"
        >
          <Shield size={15} className="text-[color:var(--ok)]" /> {match.organizer} ·{' '}
          {AUTH_BYPASS
            ? formatReliability(match.organizerMatches, match.organizerNoShows + (getPlayerActions(match.organizer).noShowsDelta ?? 0))
            : organizerPub.data
              ? formatReliability(organizerPub.data.matchesPlayed, organizerPub.data.noShows)
              : 'sin partidos registrados aún'}
        </button>

        {/* Only people who PLAYED it can record results / flag no-shows —
            the reliability signal must not be editable by strangers. */}
        {isPast &&
          ((AUTH_BYPASS
            ? match.players.some((p) => p.name === profile.name)
            : (match.playerUids?.includes(user?.uid ?? '') ?? false)) ||
          isOrganizer ? (
            <PostMatchPanel match={match} />
          ) : match.result ? (
            <p className="flex items-center gap-2 text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
              <Check size={15} className="text-[color:var(--ok)]" /> Resultado registrado — ganaron{' '}
              {match.result.winners.map((n) => n.split(' ')[0]).join(' y ')}.
            </p>
          ) : (
            <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-faint)]">
              Solo los jugadores de este partido pueden registrar el resultado.
            </p>
          ))}
      </div>

      <div ref={setBottomChrome} className={`chrome-foot safe-bottom sticky bottom-0 px-4 pt-2 pb-3 bg-[var(--chrome-bg)] border-t border-[var(--line)] flex gap-3 ${isCancelled || (isPast && !isOrganizer) ? 'hidden' : ''}`}>
        {isPast && isOrganizer ? (
          // The weekly-organizer shortcut: same club, hora, nivel — pick a new
          // date and it's out. (Research: repeat groups are THE core cadence.)
          <Button
            className="flex-1"
            onClick={() =>
              navigate('/crear', {
                state: {
                  prefill: {
                    ...(match.padelPlaceId ? { placeId: match.padelPlaceId } : {}),
                    ...(match.manualAddress ? {} : { placeName: match.place }),
                    time: match.timeLabel,
                    levelKey: LEVEL_BANDS.find((b) => b.min === match.levelMin && b.max === match.levelMax)?.key,
                    position: match.position,
                    matchType: match.matchType === 'Competitivo' ? 'competitivo' : 'casual',
                    privacy: match.privacy === 'Privado' ? 'privado' : 'abierto',
                    courtReserved: match.courtReserved,
                    ...(match.cost != null ? { price: String(match.cost).replace('.', ',') } : {}),
                    ...(match.durationMinutes ? { durationMinutes: match.durationMinutes } : {}),
                    ...(match.whatsappGroupUrl ? { whatsappGroupUrl: match.whatsappGroupUrl } : {}),
                    // 'mixed' included since 2026-08-11 — without it, repeating
                    // a Mixto match silently produced an Abierto one, losing the
                    // 2+2 constraint with no cue that anything had changed.
                    ...(match.genderCategory === 'women'
                      ? { genderCat: 'Femenino' as const }
                      : match.genderCategory === 'men'
                        ? { genderCat: 'Masculino' as const }
                        : match.genderCategory === 'mixed'
                          ? { genderCat: 'Mixto' as const }
                          : {}),
                    // Same match, same people: the previous roster rides along
                    // and the new match auto-opens the invite sheet preselected.
                    reinvite: match.players
                      .map((p, i) => ({ uid: match.playerUids?.[i] ?? '', name: p.name }))
                      .filter((e) => e.uid && e.uid !== match.organizerUid),
                  },
                },
              })
            }
          >
            <RotateCcw size={18} /> Repetir partido
          </Button>
        ) : isOrganizer ? (
          <>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => navigate(`/editar-partido/${match.id}`)}
            >
              <Pencil size={18} /> Editar
            </Button>
            <Button
              variant="outline"
              className="flex-1 !text-[color:var(--bad)] !border-[var(--bad)]"
              onClick={() => setCancelSheet(true)}
            >
              <X size={18} /> Cancelar
            </Button>
          </>
        ) : joined ? (
          // One control was doing two opposite jobs: it WORE mint (styleguide
          // §3 — positive outcomes) while its tap LEAVES the match, and it read
          // as a status pill rather than a button. Split them: mint states the
          // fact and isn't tappable; the departure is a quiet neutral button.
          <>
            <p className="flex-1 flex items-center gap-2 min-h-[var(--tap)] text-[length:var(--fs-sm)] font-semibold text-[color:var(--ok)]">
              <Check size={18} /> Estás dentro
            </p>
            <Button
              variant="outline"
              onClick={() => void toggleJoin()}
              className="shrink-0 !text-[color:var(--fg-muted)]"
            >
              Salir
            </Button>
          </>
        ) : (
          // A `privacy === 'Privado'` branch used to sit here and replace this
          // button with "el organizador debe invitarte" on every private match.
          // The server no longer denies that join (privado = unlisted,
          // 2026-08-08), so the message was the only thing still blocking it.
          <Button className="flex-1" disabled={full} onClick={() => void toggleJoin()}>
            {full ? 'Completo' : 'Unirme al partido'}
          </Button>
        )}
      </div>

      {copied && (
        <p className="um-enter fixed bottom-24 left-1/2 -translate-x-1/2 z-[60] px-4 py-2 rounded-full bg-[var(--surface-card)] border border-[var(--line)] text-[length:var(--fs-sm)] font-medium shadow-lg">
          Enlace copiado
        </p>
      )}

      <PlayerProfileSheet
        name={profileFor?.name ?? null}
        uid={profileFor?.uid ?? null}
        onClose={() => setProfileFor(null)}
      />

      {isOrganizer && (
        <ContactInviteSheet
          match={match}
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          preselect={reinvite ?? []}
        />
      )}

      <BottomSheet open={shareFallback} onClose={() => setShareFallback(false)} title="Compartir partido">
        <div className="flex flex-col gap-3 pb-2">
          <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
            No pudimos abrir el menú de compartir. Copia el enlace a mano:
          </p>
          <input
            className="field"
            readOnly
            value={matchShareUrl(match.id)}
            onFocus={(e) => e.currentTarget.select()}
          />
          <span className="text-[length:var(--fs-xs)] text-[color:var(--fg-faint)]">
            Mantén pulsado (o toca) el enlace para seleccionarlo y copiarlo.
          </span>
        </div>
      </BottomSheet>

      <BottomSheet open={!!kickFor} onClose={() => setKickFor(null)} title="Quitar jugador">
        <div className="flex flex-col gap-3 pb-2">
          <p className="text-[color:var(--fg-muted)]">
            {kickFor?.split(' ')[0]} saldrá del partido, su plaza quedará libre y recibirá un aviso.
          </p>
          <Button
            className="!bg-[var(--bad)]"
            onClick={() => {
              void (async () => {
                if (!kickFor) return
                navigator.vibrate?.(15)
                if (AUTH_BYPASS) {
                  overrideMatch(match.id, { players: match.players.filter((pl) => pl.name !== kickFor) })
                  setKickFor(null)
                  showToast('Jugador quitado del partido')
                  return
                }
                // Kick = the organizer running the leave transaction for the
                // kicked player's uid (same patch, same rules-validated shape).
                const idx = match.players.findIndex((pl) => pl.name === kickFor)
                const kickedUid = match.playerUids?.[idx]
                if (!kickedUid) return
                try {
                  await leaveMutation.mutateAsync({ matchId: match.id, uid: kickedUid })
                  setKickFor(null)
                  showToast('Jugador quitado del partido')
                } catch {
                  setKickFor(null)
                  navigator.vibrate?.(20)
                  showToast('No pudimos quitar al jugador. Inténtalo de nuevo.')
                }
              })()
            }}
          >
            <X size={18} /> Sí, quitar
          </Button>
          <Button variant="outline" onClick={() => setKickFor(null)}>
            Volver
          </Button>
        </div>
      </BottomSheet>

      <BottomSheet open={leaveSheet} onClose={() => setLeaveSheet(false)} title="Salir del partido">
        <div className="flex flex-col gap-3 pb-2">
          <p className="text-[color:var(--fg-muted)]">
            Tu plaza quedará libre y el organizador recibirá un aviso.
          </p>
          <Button className="!bg-[var(--bad)]" onClick={() => void confirmLeave()}>
            <X size={18} /> Sí, salir
          </Button>
          <Button variant="outline" onClick={() => setLeaveSheet(false)}>
            Volver
          </Button>
        </div>
      </BottomSheet>

      <BottomSheet
        open={seatingName !== null}
        onClose={() => setSeatingName(null)}
        // The title must not promise what the body refuses. A sheet headed
        // "Apuntar a alguien" that then says you cannot is a small lie told at
        // the exact moment the person is trying to do the thing.
        title={vouchBlocked ? 'Sin plazas por ahora' : 'Apuntar a alguien'}
      >
        <div className="flex flex-col gap-3 pb-2">
          {/* Never offer a form that cannot succeed. Letting someone type a
              friend's name and THEN telling them they had no room is the worst
              order to learn it in — and the reason is about them, so it should
              arrive before the effort, not after. */}
          {vouchBlocked ? (
            <>
              <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
                Ahora mismo no te quedan plazas para apuntar a nadie.
                {(myPub.data?.guestNoShows ?? 0) > 0
                  ? ' Recuperas una cuando alguno de los que has traído juegue.'
                  : ' Recuperas una cuando se juegue alguno de los partidos donde ya has apuntado a alguien.'}
              </p>
              <Button variant="outline" size="lg" onClick={() => setSeatingName(null)}>
                Entendido
              </Button>
            </>
          ) : (
            <>
              <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
                Ocupa la plaza ya. Le mandamos el enlace para que confirme cuando
                quiera — y si no aparece, cuenta como tuyo.
              </p>
              <input
                className="field"
                placeholder="Nombre"
                autoComplete="off"
                value={seatingName ?? ''}
                onChange={(e) => setSeatingName(e.target.value)}
              />
              <Button
                size="lg"
                disabled={!seatingName?.trim() || addPlaceholder.isPending}
                aria-busy={addPlaceholder.isPending}
                onClick={() => void onSeatSomeone()}
              >
                {addPlaceholder.isPending ? 'Guardando…' : 'Ocupar la plaza'}
              </Button>
            </>
          )}
        </div>
      </BottomSheet>

      <BottomSheet open={cancelSheet} onClose={() => setCancelSheet(false)} title="Cancelar partido">
        <div className="flex flex-col gap-3 pb-2">
          <p className="text-[color:var(--fg-muted)]">
            Se avisará a {players.length - 1} jugador{players.length - 1 === 1 ? '' : 'es'}. Esta
            acción no se puede deshacer.
          </p>
          <Button className="!bg-[var(--bad)]" onClick={() => void confirmCancel()}>
            <X size={18} /> Sí, cancelar partido
          </Button>
          <Button variant="outline" onClick={() => setCancelSheet(false)}>
            Volver
          </Button>
        </div>
      </BottomSheet>
      <NeedsGenderSheet
        open={genderSheet}
        onClose={() => setGenderSheet(false)}
        uid={user?.uid ?? null}
        category={
          match.genderCategory === 'men' ||
          match.genderCategory === 'women' ||
          match.genderCategory === 'mixed'
            ? match.genderCategory
            : null
        }
        onSaved={(gender) => {
          // Retry with the value we just saved — see the note on toggleJoin.
          // The refetch still runs so the rest of the screen catches up, but
          // the join no longer waits on it.
          void myDoc.refetch()
          void toggleJoin(gender)
        }}
      />
    </>
  )
}

function Info({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[color:var(--fg-faint)]">{icon}</span>
      <span>
        <span className="block text-[length:var(--fs-xs)] text-[color:var(--fg-muted)]">{label}</span>
        <span className="block font-semibold">{value}</span>
      </span>
    </div>
  )
}
